import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ClipboardList, Info, PlayCircle, Eye, CheckCircle, ListCheck } from "lucide-react";
import TopBar from "../components/TopBar";
import './css/MCQTestDetails.css';
import axios from "./../axios";

const MCQTestDetails = ({ setSideBar, setSettingDrawer }) => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [tests, setTests] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const token = localStorage.getItem("student_token");

        const [testsResponse] = await Promise.all([
          axios.get('student/mcq', {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        // Map API response to match component's expected structure
        const mappedTests = testsResponse.data.map(test => ({
          id: test.id.toString(), // Convert to string to match useParams
          title: test.topic, // Map 'topic' to 'title'
          subject: test.subject.toString(), // Convert subject ID to string or fetch subject name if needed
          attempted: test.attempted,
          marks: test.marks,
          instructions: test.instructions,
          description:test.details,
          subject_name:test.subject_name
          // Map 'details' to 'instructions'
        }));

        setTests(mappedTests);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const test = tests.find((t) => t.id === id);

  const handleStartTest = () => {
    if (!test) return;
    navigate(`/student/mcq-test/${test.id}/take`);
  };

  const handleViewResponse = () => {
    if (!test) return;
    navigate(`/student/mcq-test/${test.id}/response`);
  };

  if (isLoading) {
    return (
      <>
        <TopBar title="MCQ Test" setSideBar={setSideBar} setSettingDrawer={setSettingDrawer} />
        <div className="mcq-test-details-container">
          <div className="mcq-test-details-header">
            <ClipboardList size={20} className="header-icon" />
            <div className="skeleton skeleton-header"></div>
          </div>
          <div className="details-card">
            <div className="detail-field">
              <div className="field-icon">
                <ClipboardList size={18} />
              </div>
              <div className="field-content">
                <span className="field-label">Title</span>
                <div className="skeleton skeleton-text"></div>
              </div>
            </div>
            <div className="detail-field">
              <div className="field-icon">
                <ClipboardList size={18} />
              </div>
              <div className="field-content">
                <span className="field-label">Subject</span>
                <div className="skeleton skeleton-text"></div>
              </div>
            </div>
            <div className="detail-field">
              <div className="field-icon">
                <ListCheck size={18} />
              </div>
              <div className="field-content">
                <span className="field-label">Description</span>
                <div className="skeleton skeleton-paragraph"></div>
              </div>
            </div>
            <div className="detail-field">
              <div className="field-icon">
                <Info size={18} />
              </div>
              <div className="field-content">
                <span className="field-label">Instructions</span>
                <div className="skeleton skeleton-paragraph"></div>
              </div>
            </div>
            <div className="button-group">
              <div className="skeleton skeleton-button"></div>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (!test) {
    console.error(`Test with id ${id} not found`);
    return (
      <>
        <TopBar title="MCQ Test" setSideBar={setSideBar} setSettingDrawer={setSettingDrawer} />
        <div className="mcq-test-details-container">
          <div className="mcq-test-details-header">
            <ClipboardList size={20} className="header-icon" />
            <h2>Test Not Found</h2>
          </div>
          <div className="details-card">
            <div className="no-test-message">
              Sorry, the requested test could not be found. Please check the test ID or return to the tests list.
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <TopBar title="MCQ Test" setSideBar={setSideBar} setSettingDrawer={setSettingDrawer} />
      <div className="mcq-test-details-container">
        <div className="mcq-test-details-header">
          <ClipboardList size={20} className="header-icon" />
          <h2>{test.title}</h2>
        </div>
        <div className="details-card">
          <div className="detail-field">
            <div className="field-icon">
              <ClipboardList size={18} />
            </div>
            <div className="field-content">
              <span className="field-label">Title</span>
              <span className="field-value">{test.title}</span>
            </div>
          </div>
          <div className="detail-field">
            <div className="field-icon">
              <ClipboardList size={18} />
            </div>
            <div className="field-content">
              <span className="field-label">Subject</span>
              <span className="field-value">{test.subject_name}</span>
            </div>
          </div>
          {test.attempted && (
            <div className="detail-field">
              <div className="field-icon">
                <CheckCircle size={18} />
              </div>
              <div className="field-content">
                <span className="field-label">Score</span>
                <span className="field-value">{test.marks}%</span>
              </div>
            </div>
          )}
          <div className="detail-field">
            <div className="field-icon">
              <ListCheck size={18} />
            </div>
            <div className="field-content">
              <span className="field-label">Description</span>
              <p className="field-description">{test.description}</p>
            </div>
          </div>
      <div className="detail-field">
            <div className="field-icon">
              <Info size={18} />
            </div>
            <div className="field-content">
              <span className="field-label">Instructions</span>
              <p className="field-description">{test.instructions}</p>
            </div>
          </div>

          
          <div className="button-group">
            {!test.attempted && (
              <button className="start-test-button" onClick={handleStartTest}>
                <PlayCircle size={16} /> Start Test
              </button>
            )}
            {test.attempted && (
              <button className="view-response-button" onClick={handleViewResponse}>
                <Eye size={16} /> View Response
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default MCQTestDetails;